import { createColumnHelper } from '@tanstack/react-table'
import type { ParseKeys } from 'i18next'
import { FileText, Plus } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { ColumnsMenu } from '@/components/data/ColumnsMenu'
import { RecordId, RowValue } from '@/components/data/DataTable'
import { FacetFilter, FilterEmptyState } from '@/components/data/FacetFilter'
import { RecordBoard } from '@/components/data/RecordBoard'
import {
  PAGE_SIZES,
  toHiddenColumns,
  toVisibilityState,
  useViewState,
} from '@/components/data/useViewState'
import { RecordCard, RecordList } from '@/components/data/RecordList'
import { EmptyState } from '@/components/feedback/EmptyState'
import { ErrorState } from '@/components/feedback/ErrorState'
import { Skeleton } from '@/components/feedback/Skeleton'
import { PageHeader } from '@/components/layout/PageHeader'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { toOptions } from '@/components/ui/control'
import { SegmentedControl } from '@/components/ui/SegmentedControl'
import { QuoteStatusPill } from '@/components/ui/StatusPill'
import { QUOTE_TONES } from '@/lib/statusTone'
import { useFormat } from '@/i18n/useFormat'
import {
  formatMoney,
  formatNumber,
  parseDateOnly,
} from '@/lib/format'
import { placeName } from '@/lib/countries'
import { MODE_ICONS, modeLabelKey } from '@/lib/icons'
import { Toast } from '@/components/feedback/Toast'
import { useSession } from '@/features/auth/api/useSession'
import { can, CAP, reaches, SURFACE } from '@/lib/can'
import {
  useClientAnswer,
  useQuotes,
  type ClientAnswer,
} from '@/features/quotes/api/useQuote'
import { QuoteCard } from '@/features/quotes/components/QuoteCard'
import { PipelineStatistics } from '@/features/quotes/components/PipelineStatistics'
import type { QuoteStatus, QuoteSummary } from '@/types/domain'

const column = createColumnHelper<QuoteSummary>()

const VIEW_OPTIONS = [
  { value: 'table', labelKey: 'common.view.table' },
  { value: 'board', labelKey: 'common.view.board' },
] as const

/** How long an issued quote may sit before the board says so. Three working
 *  days is when ASA chase by phone, so it is when the card should mention it. */
const QUIET_DAYS = 3

/** What a lane is worth: its quotes summed, in the currency they share.
 *
 * Mixed currencies are summed as if they matched, which would be a lie — so the
 * total is withheld instead. Today every quote is USD (AD-19's sibling rule on
 * money), and this is the guard for the day that stops being true. */
function laneTotal(rows: QuoteSummary[]) {
  const priced = rows.filter((row) => row.sell)
  if (priced.length === 0) return null

  const currency = priced[0]?.sell?.currency ?? 'USD'
  if (priced.some((row) => row.sell?.currency !== currency)) return null

  const sum = priced.reduce((acc, row) => acc + Number(row.sell?.amount ?? 0), 0)
  return formatMoney(sum, currency, 0)
}

/** Board lanes in pipeline order: the working states first, the ends last.
 *  The wire's own vocabulary — "waiting on the client" IS `sent`. */
const QUOTE_LANES: readonly QuoteStatus[] = [
  'draft',
  'sent',
  'converted',
  'rejected',
  'expired',
]

/* Which columns the Columns menu offers, by the ids the table knows them as
 * (UX-DR-4). A list rather than something derived from `columns` below, because
 * that array is built inside the component against `t` and a menu built from it
 * would carry the display column too.
 *
 * `number` IS LOCKED. A list of quotes with the quote number turned off is a
 * grid of anonymous rows, and the way back to it is this same menu the reader
 * would then have to find. The `flag` display column is absent entirely: it has
 * no header, it is empty on a healthy row, and offering "hide the empty column"
 * is a choice with nothing on either side of it. */
const COLUMN_MENU: readonly { id: string; labelKey: ParseKeys; locked?: boolean }[] = [
  { id: 'number', labelKey: 'quotes.columns.number', locked: true },
  { id: 'clientName', labelKey: 'quotes.columns.client' },
  { id: 'destination', labelKey: 'quotes.columns.route' },
  { id: 'cargoDescription', labelKey: 'quotes.columns.cargo' },
  { id: 'status', labelKey: 'quotes.columns.status' },
  { id: 'validUntil', labelKey: 'quotes.columns.validUntil' },
  { id: 'sell', labelKey: 'quotes.columns.sell' },
  { id: 'margin', labelKey: 'quotes.columns.margin' },
]

/** The ids no menu entry may switch off, derived FROM the menu so the two
 *  cannot drift apart.
 *
 *  Applied to the STORED hidden list as well as to the menu, which is the part
 *  worth stating: localStorage is user-writable, and a release where a column
 *  was still optional leaves real entries behind. A stored "hide the number"
 *  would otherwise hide the identifier column with no way back — the menu
 *  renders a locked entry ticked and disabled, so it cannot be un-hidden there,
 *  and if it were the only hidden column the reset button would have nothing to
 *  count and would be disabled too. */
const LOCKED_COLUMNS: readonly string[] = COLUMN_MENU.filter(
  (entry) => entry.locked,
).map((entry) => entry.id)

/** "Nothing selected", as one shared array. A fresh `[]` per render would make
 *  the memo below recompute on every paint and defeat itself; the hook already
 *  DELETES an empty selection rather than storing one, so an absent facet and
 *  an empty one are the same fact and may share an object. */
const NO_FILTER: readonly string[] = []

/* The quotes section, behind its surface (#129, N-08).
 *
 * A role at «Нет доступа» on quotes has no Quotes stop in the menu; this is
 * the typed address, and it gets ONE refusal rather than a pipeline the server
 * would answer empty — an empty list reads as "nobody has quoted anything",
 * which is not what happened. The pipeline lives in its own component so no
 * list or statistics query fires for a refused reader. Reach is the session's
 * (`reaches()`, never a role name or a capability); the server scopes every
 * read regardless (AD-21). While the session loads a skeleton stands in, so
 * the refusal never flashes at a reader who has the surface. */
export function QuotesPage() {
  const { t } = useTranslation()
  const { data: session, isPending: sessionPending } = useSession()

  if (sessionPending) {
    return (
      <>
        <PageHeader title={t('quotes.title')} subtitle={t('quotes.subtitle')} />
        <Skeleton className="h-64 rounded-[var(--radius-DEFAULT)]" />
      </>
    )
  }

  if (!reaches(session, SURFACE.quotes)) {
    return (
      <>
        <PageHeader title={t('quotes.title')} />
        <Card>
          <EmptyState
            title={t('quotes.noAccess.title')}
            description={t('quotes.noAccess.description')}
          />
        </Card>
      </>
    )
  }

  return <QuotesPipeline canCreate={can(session, CAP.quoteCreate)} />
}

function QuotesPipeline({ canCreate }: { canCreate: boolean }) {
  const { t, i18n } = useTranslation()
  const format = useFormat()
  const navigate = useNavigate()
  /* The view, the sort, the hidden columns and the status filter, per user and
     per surface — in localStorage, because they are a workstation habit and not
     a profile setting (00-decisions.md § 2). Nothing here calls a preferences
     endpoint; there is none, and this issue does not add one. */
  const [viewState, controls] = useViewState('quotes')

  const { data, isPending, isError, error, refetch, dataUpdatedAt } = useQuotes()
  const answer = useClientAnswer()

  /* The status facet, applied CLIENT-SIDE over the rows the server already
     decided to send. That direction is the whole rule (AD-5, AD-21): a filter
     here can only ever narrow a set the scope predicate fixed, it can never
     widen it, and no value it holds is ever forwarded as a query parameter. So
     there is no "show all owners" affordance on this screen and there cannot
     be one — a quote a caller may not read never reached this component, and
     nothing the user selects in a popover can fetch it. */
  const statusFilter = viewState.filters.status ?? NO_FILTER

  const rows = useMemo(() => {
    const all = data ?? []
    if (statusFilter.length === 0) return all
    return all.filter((row) => statusFilter.includes(row.status))
  }, [data, statusFilter])

  /* The hook stores what the user turned OFF; TanStack wants a map of what is
     on. The translation is useViewState's, called here rather than re-derived,
     so a column added next release is visible to a user who has never seen it
     instead of inheriting somebody's old "off". */
  const columnVisibility = useMemo(
    () =>
      toVisibilityState(
        viewState.hiddenColumns.filter((id) => !LOCKED_COLUMNS.includes(id)),
      ),
    [viewState.hiddenColumns],
  )

  /* The undo moment after a drag completed: which quote moved, and the answer
     that would put it back. One at a time — a second drag replaces the toast,
     because two stacked undos invite undoing the wrong one. */
  const [undo, setUndo] = useState<{
    number: string
    id: string
    restore: ClientAnswer
    to: QuoteStatus
  } | null>(null)

  /* The board's drag policy.

     The ANSWER FAMILY — sent, rejected — moves freely on drop and completes
     with undo, because recording the client's word has no side effects; the
     server bounds the same family (MayAnswer), so this list is a mirror of a
     rule, not the rule.

     TWO transitions are real acts with real side effects, and NEITHER
     completes on drop — each opens the record instead, where the act's own
     flow lives:

       · Draft → Sent writes a PDF, an email and an immutable revision, and
         the record is where the recipient check is.
       · Sent → Converted creates the case, its rate, its planned lines and
         its documents in one transaction (FR-19, AD-14), and the record is
         where the button that does it — and the case number it returns —
         belong.

     The second one is why the board no longer has an Approved lane. Accepting
     an offer used to drop into a lane that recorded a status and made no case,
     which was a drag gesture that half-agreed on the company's behalf; the
     lane and the status behind it are gone (migration 0050), and the card now
     travels from Sent straight to Converted through the record.

     Everything else refuses at the gesture: a draft has no client answer to
     record, converted is terminal, a rejected quote has nothing to convert,
     and expired is COMPUTED (AD-24) — there is no stored state to drag into,
     and an expired card's own stored status is hidden behind the display
     value, so an undo could not name what to restore. Late answers on an
     expired quote go through the record page.

     THE SERVER HOLDS THE SAME PARTITION AND IT LEADS (AD-24):
     `quote.HasSideEffects` in backend/internal/quote/domain.go defines it as
     the answer family's complement, exactly as this list does, and answers TRUE
     for any pair nobody has classified — so a transition invented later opens
     the record and asks a human instead of completing silently on a drop. The
     rule therefore lives in two places, deliberately, because the board must
     decide before the gesture finishes and a round trip per hover is not a
     gesture. The next person changing the lifecycle changes both, and the
     server's copy is the one that decides. */
  const ANSWER_FAMILY: readonly QuoteStatus[] = ['sent', 'rejected']
  const ANSWER_OF: Partial<Record<QuoteStatus, ClientAnswer>> = {
    sent: 'pending',
    rejected: 'rejected',
  }

  const canMoveTo = (row: QuoteSummary, lane: QuoteStatus): boolean => {
    if (row.status === 'draft') return lane === 'sent'
    // Only a SENT quote converts, which is the server's own rule
    // (quote.ReadForConversion). A rejected card lighting up over Converted
    // would invite a gesture the record page then refuses.
    if (lane === 'converted') return row.status === 'sent'
    return ANSWER_FAMILY.includes(row.status) && ANSWER_FAMILY.includes(lane)
  }

  /* What an empty lane says. "No quotes" always; the drag invitation only over
     a lane that some card on this board could actually be dropped into, which
     is `canMoveTo` asked of every row rather than a second copy of the policy
     — a hint that does not work where it is printed teaches a gesture the
     board refuses, and the refusal arrives as a not-allowed cursor with no
     explanation. So Converted and Expired say only that they are empty. */
  const laneEmpty = (lane: QuoteStatus) => {
    const droppable = rows.some((row) => canMoveTo(row, lane))
    return (
      <div className="flex flex-col gap-1">
        <p className="text-text-muted text-[12.5px] leading-snug font-light">
          {t('quotes.board.laneEmpty')}
        </p>
        {droppable ? (
          <p className="text-text-faint text-[12px] leading-snug font-light">
            {t('quotes.board.laneDrop')}
          </p>
        ) : null}
      </div>
    )
  }

  const moveCard = (row: QuoteSummary, lane: QuoteStatus) => {
    if (row.status === 'draft' || lane === 'converted') {
      // Side effects do not complete on drop — the record opens instead.
      // Sending and converting are both in this branch, and converting is the
      // sharper case of the two: the act mints a case number this screen
      // cannot guess, so there is nothing honest to show optimistically.
      navigate(`/quotes/${row.id}`)
      return
    }
    const to = ANSWER_OF[lane]
    const restore = ANSWER_OF[row.status]
    if (!to || !restore) return
    answer.mutate({ id: row.id, answer: to })
    setUndo({ number: row.number, id: row.id, restore, to: lane })
  }

  const columns = useMemo(
    () => [
      column.accessor('number', {
        header: t('quotes.columns.number'),
        size: 108,
        cell: (info) => <RecordId>{info.getValue()}</RecordId>,
      }),
      column.accessor('clientName', {
        header: t('quotes.columns.client'),
        cell: (info) => (
          <RowValue
            primary={info.getValue()}
            secondary={info.row.original.ownerName}
          />
        ),
      }),
      column.accessor('destination', {
        header: t('quotes.columns.route'),
        cell: (info) => {
          const { origin, destination, mode } = info.row.original
          // A header-only quote has no lane yet: the route is a route option
          // (FR-3, Story 2.4). An em dash rather than an empty cell, so the
          // column reads as "not entered" rather than as a rendering fault.
          if (!origin && !destination) {
            return <span className="text-text-faint">{t('common.empty')}</span>
          }
          const Icon = mode ? MODE_ICONS[mode] : undefined
          return (
            <span className="flex items-center gap-2">
              {Icon ? (
                <Icon size={15} className="text-text-muted shrink-0" aria-hidden />
              ) : null}
              <RowValue
                primary={
                  <>
                    {placeName(origin, i18n.language)}
                    <span className="text-text-faint mx-1.5">→</span>
                    {placeName(destination, i18n.language)}
                  </>
                }
                secondary={mode ? t(modeLabelKey(mode)) : undefined}
              />
            </span>
          )
        },
      }),
      /* What is being shipped, in the words the salesperson typed at creation.
         It is the quote header's one-line answer to "what is this" — a first
         description, entered before a single position has been weighed — and
         this list is where it belongs: it is how the team recognises a row
         they are looking for. It is deliberately NOT on the client's offer,
         where the Cargo tab's declared positions are the only account of the
         consignment (see quote.cargoSummaryText) — a guess printed beside
         measured positions is the one a client reads when the two disagree.

         Truncated to one line with the full text on hover: a description runs
         to a thousand characters, and a row that grows to fit one breaks the
         scanning rhythm of every row above it. */
      column.accessor((row) => row.cargoDescription ?? '', {
        id: 'cargoDescription',
        header: t('quotes.columns.cargo'),
        size: 220,
        cell: (info) => {
          const description = info.getValue()
          if (!description) {
            return <span className="text-text-faint">{t('common.empty')}</span>
          }
          return (
            <span className="block truncate" title={description}>
              {description}
            </span>
          )
        },
      }),
      column.accessor('status', {
        header: t('quotes.columns.status'),
        size: 148,
        cell: (info) => <QuoteStatusPill status={info.getValue()} />,
      }),
      column.accessor('validUntil', {
        header: t('quotes.columns.validUntil'),
        size: 112,
        // Right-aligned but not tabular — see the ETA column in CasesPage.
        meta: { headerClassName: 'text-right', cellClassName: 'text-right' },
        cell: (info) => format.date(info.getValue()),
      }),
      column.accessor((row) => row.sell?.amount, {
        id: 'sell',
        header: t('quotes.columns.sell'),
        size: 116,
        meta: { numeric: true },
        cell: (info) => formatNumber(info.getValue(), 2),
      }),
      column.accessor((row) => row.margin?.amount, {
        id: 'margin',
        header: t('quotes.columns.margin'),
        size: 128,
        meta: { numeric: true },
        cell: (info) => (
          <RowValue
            primary={
              <span className="asa-num block text-right">
                {formatNumber(info.getValue(), 2)}
              </span>
            }
            secondary={
              <span className="asa-num block text-right">
                {/* Two decimals (O4 / L19), as the costing prints the same
                    figure; an absent margin keeps its em dash. */}
                {info.row.original.marginPercent
                  ? `${formatNumber(info.row.original.marginPercent, 2)}%`
                  : t('common.empty')}
              </span>
            }
          />
        ),
      }),
      column.display({ id: 'flag', header: '', meta: { flag: true }, cell: () => null }),
    ],
    [t, format, i18n.language],
  )

  /* The two compact forms of a quote are NOT the same card.

     The phone list keeps the generic RecordCard: it replaces a table ROW, and a
     row's job is identification \u2014 which quote, for whom, worth what. The board
     card is a richer thing (QuoteCard), because a lane is read to decide what
     to chase, and that needs the clock and the blocker too. */
  const listCard = (row: QuoteSummary) => (
    <RecordCard
      id={row.number}
      status={<QuoteStatusPill status={row.status} />}
      primary={row.clientName}
      secondary={
        row.origin && row.destination
          ? `${placeName(row.origin, i18n.language)} \u2192 ${placeName(row.destination, i18n.language)}`
          : format.date(row.quoteDate)
      }
      value={formatNumber(row.margin?.amount, 2)}
      valueLabel={t('quotes.card.margin')}
    />
  )

  /* What is holding a quote up, in the words the lane needs.

     Two kinds, and the order between them is the point. A blocked checklist
     outranks client silence because it is the one the TEAM can fix today:
     chasing a client about an offer that cannot legally go out is the wasted
     call this board exists to prevent. Only one flag is shown \u2014 a card with two
     warnings makes neither urgent.

     The blocker is a COUNT from the list endpoint, so the card says that
     something is missing and the quote itself says what (FR-3): the sentence a
     salesperson fixes belongs beside the position it is about. */
  const quoteFlag = (row: QuoteSummary) => {
    const blocked = row.cargo?.blocked ?? 0
    if (blocked > 0) {
      return {
        label: t('quotes.card.blocked', { count: blocked }),
        tone: 'attention' as const,
      }
    }

    if (row.status !== 'sent') return undefined
    if (!row.quoteDate) return undefined

    /* Aged from when the LIST was read, not from when this browser painted it.
       One instant for every card in the board — the reasoning the server
       applies to expiry, where ToQuoteDTO takes its day as a parameter so a
       list cannot straddle midnight and judge two quotes differently. It also
       keeps the clock out of render entirely: a tab left open overnight shows
       the age of the data it is actually displaying, and moves when refetched. */
    const days = Math.floor(
      (dataUpdatedAt - parseDateOnly(row.quoteDate).getTime()) / 86_400_000,
    )
    if (days < QUIET_DAYS) return undefined

    return { label: t('quotes.card.noAnswer', { count: days }), tone: 'muted' as const }
  }

  /* Two different facts, and telling them apart is the point (UX-DR-17).
     "You have no quotes" over a list a filter emptied says the user's data is
     gone; the filtered state says what happened and carries the way out.

     A filter over an ALREADY empty server list narrowed nothing, so the second
     condition matters: a persisted facet would otherwise meet a first-day user
     with "nothing matches this filter" and a Clear button that reveals an empty
     list, instead of the empty state that offers to create the first quote. */
  const empty =
    statusFilter.length > 0 && (data ?? []).length > 0 ? (
      <FilterEmptyState onClear={controls.clearFilters} />
    ) : (
      <EmptyState
        icon={FileText}
        title={t('quotes.emptyTitle')}
        description={t('quotes.emptyDescription')}
      />
    )

  return (
    <>
      <PageHeader
        title={t('quotes.title')}
        subtitle={t('quotes.subtitle')}
        actions={
          <>
            {/* The facet narrows both views, so it sits outside the toggle. */}
            <FacetFilter
              label={t('common.filters.status')}
              options={toOptions(QUOTE_LANES, (status) => t(`quoteStatus.${status}`))}
              selected={statusFilter}
              onChange={(values) => controls.setFilter('status', values)}
            />
            {/* Columns are a fact about the TABLE. The board groups cards into
                lanes and has no columns to hide, so offering the menu there
                would be a control whose every choice does nothing visible. */}
            {viewState.view === 'table' ? (
              <ColumnsMenu
                columns={COLUMN_MENU.map((entry) => ({
                  id: entry.id,
                  label: t(entry.labelKey),
                  locked: entry.locked,
                }))}
                hidden={viewState.hiddenColumns}
                onToggle={controls.toggleColumn}
                onReset={() => controls.setHiddenColumns([])}
              />
            ) : null}
            <SegmentedControl
              label={t('common.view.label')}
              size="md"
              options={VIEW_OPTIONS}
              value={viewState.view}
              onChange={controls.setView}
            />
            {/* Creation opens the stepped page, not a sheet: the whole of UJ-1
                — terms, cargo, routes, price, send — lives on one surface.
                Absent without `quote.create` (#128, N-07): the server refuses
                the create on that key alone, so the button would only lead to
                a form whose submit is a 403. */}
            {canCreate ? (
              <Button iconLeft={Plus} onClick={() => navigate('/quotes/new')}>
                {t('quotes.actions.newQuote')}
              </Button>
            ) : null}
          </>
        }
      />

      {/* Above the pipeline, because the figures are what the reader came to
          check before they start chasing rows. Its own query and its own error
          state: statistics that could not be read must not take the list down
          with them. */}
      <PipelineStatistics />

      <Card>
        {isPending ? (
          <Skeleton className="h-64 rounded-[var(--radius-DEFAULT)]" />
        ) : isError ? (
          <ErrorState error={error} onRetry={() => void refetch()} />
        ) : viewState.view === 'board' ? (
          <RecordBoard
            label={t('quotes.title')}
            data={rows}
            /* The lane's tint comes from the SAME map the pill in its heading
               reads, so the colour of a column and the colour of the label at
               the top of it cannot part company. */
            lanes={QUOTE_LANES.map((status) => ({
              value: status,
              header: <QuoteStatusPill status={status} />,
              tone: QUOTE_TONES[status],
            }))}
            groupBy={(row) => row.status}
            getRowId={(row) => row.id}
            onCardClick={(row) => navigate(`/quotes/${row.id}`)}
            canMoveTo={canMoveTo}
            onMoveCard={moveCard}
            renderCard={(row) => (
              <QuoteCard quote={row} flag={quoteFlag(row)} />
            )}
            laneTotal={laneTotal}
            laneEmpty={laneEmpty}
            empty={empty}
          />
        ) : (
        /* Below the table breakpoint a row becomes a card. Changing the
            pattern is how a narrow screen is solved; a sideways-scrolling grid
            is prohibited. */
        <RecordList
          label={t('quotes.title')}
          data={rows}
          columns={columns}
          getRowId={(row) => row.id}
          onRowClick={(row) => navigate(`/quotes/${row.id}`)}
          renderCard={listCard}
          empty={empty}
          /* Sort and column visibility are CONTROLLED from the stored view
             state, so the order a user left the list in and the columns they
             turned off are still there tomorrow (UX-DR-4). Both callbacks
             receive the resolved next value — DataTable resolves TanStack's
             updater before it calls out — which is what lets the persisted
             setters take a plain value. */
          sorting={viewState.sorting}
          onSortingChange={controls.setSorting}
          columnVisibility={columnVisibility}
          onColumnVisibilityChange={(next) =>
            controls.setHiddenColumns(toHiddenColumns(next))
          }
          /* Paged, and the page size is remembered beside the sort and the
             hidden columns — the same kind of workstation habit (UX-DR-4).
             The count is the QUOTES the reader is looking at, which is the
             filtered set: the footer sits under this list and answers for it,
             not for the set the facet is holding back. */
          pagination={{
            pageSize: viewState.pageSize,
            onPageSizeChange: controls.setPageSize,
            pageSizeOptions: PAGE_SIZES,
            summary: (total) => t('quotes.count', { count: total }),
          }}
        />
        )}
      </Card>

      {/* The drag's receipt. The message names the quote and where it went;
          the action posts the PREVIOUS answer back through the same act, so
          the undo is bookkeeping corrected, never a second mechanism. */}
      <Toast
        open={undo !== null}
        message={
          undo
            ? t('quotes.board.moved', {
                number: undo.number,
                status: t(`quoteStatus.${undo.to}`),
              })
            : ''
        }
        actionLabel={t('common.actions.undo')}
        onAction={() => {
          if (!undo) return
          answer.mutate({ id: undo.id, answer: undo.restore })
          setUndo(null)
        }}
        onDismiss={() => setUndo(null)}
      />
    </>
  )
}
